<template>
  <div class="msp-allenamento-preview">
    <h2 class="preview-titolo">
      <i v-if="showIcon" :class="'msp-icon-sport-' + allenamento.sport_id" class="preview-sport-icon" aria-hidden="true"></i>
      {{ allenamento.titolo }}
    </h2>

    <div v-if="hasGraficoAndature(allenamento)" class="grafico-andature">
      <msp-grafico-andature
        :dettagli="allenamento.dettagli"
        :zone-attive="zoneAttive"
        :sport_id="allenamento.sport_id"
        :colore="color"
        :espandi="true"
      ></msp-grafico-andature>
    </div>

    <div v-if="hasComputing(allenamento.sport_id)" class="allenamento-dati mb-3">
      <span class="dati-item">
        <span class="dati-label">
          <span class="msp-icon-time" aria-hidden="true"></span>
        </span>
        <span class="dati-value">{{ allenamento.totalone && secondsToHms(allenamento.totalone.sec) }}</span>
      </span>
      <span class="dati-item" v-if="allenamento.totalone && allenamento.totalone.mt">
        <span class="dati-label">
          <span class="msp-icon-distance" aria-hidden="true"></span>
        </span>
        <span class="dati-value">{{ renderDistanza(allenamento) }}</span>
      </span>
      <span class="dati-item" v-if="allenamento.trimp">
        <span class="dati-label">trimp: </span>
        <span class="dati-value">{{ formatTrimp(allenamento.trimp) }}</span>
      </span>
    </div>

    <div class="select-detail-mode mb-2">
      <v-chip small color="#fff" v-if="hasZone(allenamento.sport_id) && !soloAndature" class="switch-mode">
        <v-icon>mdi-text</v-icon>
        <v-switch v-model="detailAutoMode" @change="saveAutoMode"></v-switch>
        <v-icon>mdi-chart-histogram</v-icon>
      </v-chip>
      <v-chip small color="#fff" v-if="isPalestra(allenamento.sport_id)" class="switch-mode">
        <v-icon>mdi-arrow-collapse-vertical</v-icon>
        <v-switch v-model="expandedMode"></v-switch>
        <v-icon>mdi-arrow-expand-vertical</v-icon>
      </v-chip>
    </div>

    <component
      v-if="detailComponent"
      :is="detailComponent"
      :allenamento="allenamento"
      :tests="tests"
      :class="{'auto': detailAutoMode || soloAndature, 'collapsed': !expandedMode}"
    ></component>

    <div v-if="allenamento.note" class="note-allenatore pre">
      {{ allenamento.note }}
    </div>

    <div v-if="allenamento.url_youtube">
      <msp-videos :single="true" :can-edit="false" v-model="allenamento.url_youtube"></msp-videos>
    </div>
  </div>
</template>

<script>
import allenamentoCalcoli from "@src/js/vue/mixins/allenamentoCalcoli.js";
import env from "@mixins/environment.js";
import mspVideos from '@components/mspVideos.vue';
import dettaglioPodismo from '@components/dettaglioPodismo.vue';
import dettaglioSci from '@components/dettaglioSci.vue';
import dettaglioCiclismo from '@components/dettaglioCiclismo.vue';
import dettaglioNuoto from '@components/dettaglioNuoto.vue';
import dettaglioAlimentazione from '@components/dettaglioAlimentazione.vue';
import dettaglioCustom from '@components/dettaglioCustom.vue';
import dettaglioPalestra from '@components/dettaglioPalestra.vue';
import mspGraficoAndature from '@components/MspGraficoAndature.vue';
import { isDescrizioneAutomatica } from "@shared/msp/descrizioneAutomatica.js";

export default {
  name: 'MspAllenamentoPreview',
  components: {
    dettaglioPodismo,
    dettaglioSci,
    dettaglioCiclismo,
    dettaglioNuoto,
    dettaglioAlimentazione,
    dettaglioCustom,
    dettaglioPalestra,
    mspGraficoAndature,
    mspVideos,
  },
  mixins: [env, allenamentoCalcoli],
  props: {
    allenamento: { type: Object, required: true },
    tests:       { type: Array,  default: () => [] },
    zoneAttive:  { type: Array,  default: () => [] },
    showIcon:    { type: Boolean, default: false },
  },
  data: () => ({
    detailAutoMode: false,
    expandedMode:   true,
    color:          true,
  }),
  computed: {
    // Allenamento con descrizione automatica: solo il box andature, senza switch testo/andature.
    descrizioneAutomatica() {
      return isDescrizioneAutomatica(this.allenamento);
    },
    // Nessun blocco con note scritte: il testo sarebbe vuoto, si mostra solo il box andature.
    hasNoteScritte() {
      return (this.allenamento.dettagli || []).some(d => d.note_attivita && d.note_attivita.trim().length);
    },
    soloAndature() {
      if (!this.hasZone(this.allenamento.sport_id)) return false;
      return this.descrizioneAutomatica || !this.hasNoteScritte;
    },
    detailComponent() {
      if (!this.allenamento.sport_codice) return null;
      return 'dettaglio-' + this.allenamento.sport_codice;
    },
  },
  mounted() {
    const stored = localStorage.getItem('autoMode');
    this.detailAutoMode = stored === 'true';
  },
  methods: {
    saveAutoMode() {
      localStorage.setItem('autoMode', this.detailAutoMode);
    },
  },
};
</script>

<style lang="scss">
.msp-allenamento-preview {
  padding: 12px;

  .preview-titolo {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 1.2em;
    line-height: 1;
    font-weight: bold;
    margin-bottom: 10px;

    .preview-sport-icon {
      flex-shrink: 0;
      font-size: 1.2em;
    }
  }

  .grafico-andature {
    display: flex;
    height: 50px;
    margin-bottom: 8px;
    .msp-grafico-andature {
      max-width: 500px;
    }
  }

  .allenamento-dati {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    .dati-item {
      display: flex;
      align-items: center;
      white-space: nowrap;
      gap: 4px;
    }
    .dati-value {
      font-weight: bold;
    }
  }

  .note-allenatore {
    padding: 5px;
    border: 2px solid var(--col-ui-evidenza);
    border-radius: 5px;
    margin: 5px 0;
    background: var(--col-ui-evidenza-tint);
  }

  .allenamento-dettaglio {
    .msp-andature-dettaglio {
      display: none;
    }
    .note-attivita {
      display: block;
    }
    &.collapsed {
      .allenamento-palestra-col2,
      .allenamento-palestra-col3 {
        display: none !important;
      }
    }
    &.auto {
      .msp-andature-dettaglio {
        display: block;
      }
      .note-attivita {
        display: none !important;
      }
    }
  }
}
</style>
